import { useEffect } from "react";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { useFlightRestrictionZoneQueryV1 } from "@/api/v1/flight-restriction-zone-api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Calendar, 
  MapPin, 
  Shield, 
  ArrowLeft,
  Copy,
  Info,
  RefreshCw,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { ZoneDetailMap } from "@/components/map/zone-detail-map";
import { ZoneNotes } from "@/components/flight-restriction-zones/zone-notes";
import { apiTypeToLabel } from "@/lib/zone-type-utils";
import type { SearchParams } from "nuqs";

export const FlightRestrictionZoneDetailRoute = () => {
  const { zoneSlug } = useParams({ from: "/flight-restriction-zones/$zoneSlug" });
  const navigate = useNavigate();
  
  // zoneSlug is the designator in v1 API
  const designator = zoneSlug || '';
  
  const { 
    data: zone, 
    isLoading, 
    error,
    refetch
  } = useFlightRestrictionZoneQueryV1(designator);

  // Handle invalid designator
  useEffect(() => {
    if (!designator) {
      navigate({ to: '/flight-restriction-zones' });
    }
  }, [designator, navigate]);

  // Handle case when zone is not found
  useEffect(() => {
    if (!isLoading && error) {
      // Zone not found or error, redirect to main page
      navigate({ to: '/flight-restriction-zones' });
    }
  }, [isLoading, error, navigate]);

  const getTypeIcon = (typeCode: string) => {
    const type = apiTypeToLabel(typeCode);
    switch (type) {
      case "Prohibited":
        return "🚫";
      case "Restricted":
        return "⚠️";
      case "Danger":
        return "⚠️";
      default:
        return "📍";
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Flight Restrictions", href: "/flight-restriction-zones" },
              { label: "Loading..." }
            ]}
            className="mb-6"
          />
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-muted-foreground">Loading zone data...</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error || !zone) {
    return (
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Flight Restrictions", href: "/flight-restriction-zones" },
              { label: "Error" }
            ]}
            className="mb-6"
          />
          <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">Failed to load zone</p>
                <p>The flight restriction zone you're looking for could not be found or there was an error loading the data.</p>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => refetch()} variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                  <Button asChild size="sm">
                    <Link to="/flight-restriction-zones">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Zones
                    </Link>
                  </Button>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
               <BreadcrumbNav
                 items={[
                   { label: "Home", href: "/" },
                   { label: "Flight Restrictions", href: "/flight-restriction-zones" },
                   { label: zone?.name || 'Loading...' }
                 ]}
                 className="mb-6"
               />
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" asChild className="h-10 w-10">
              <Link to="/flight-restriction-zones">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
                   <div className="flex items-center gap-3">
                     <span className="text-2xl sm:text-3xl">{getTypeIcon(zone?.type || '')}</span>
                     <div>
                       <h1 className="text-xl sm:text-2xl font-semibold">{zone?.name || 'Unknown Zone'}</h1>
                       <p className="text-sm text-muted-foreground">
                         {zone?.designator} • {zone?.designator_icao}
                       </p>
                     </div>
                   </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/map" search={{ zone: designator } as SearchParams}>
                <MapPin className="h-4 w-4 mr-2" />
                View on Map
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  // Create URL to the map with this zone selected
                  const url = new URL("/map", window.location.origin);
                  url.searchParams.set("zone", designator);
                  await navigator.clipboard.writeText(url.toString());
                  toast.success("Link copied to clipboard");
                } catch {
                  toast.error("Failed to copy link");
                }
              }}
              className="flex items-center gap-2"
            >
              <Copy className="h-4 w-4" />
              Share
            </Button>
          </div>
        </div>

        {/* Status and Type */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge 
            variant="outline" 
            className={`text-sm ${
              apiTypeToLabel(zone?.type || '') === "Restricted" 
                ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
                : apiTypeToLabel(zone?.type || '') === "Danger"
                ? "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800"
                : apiTypeToLabel(zone?.type || '') === "Prohibited"
                ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
                : ""
            }`}
          >
            {zone?.type ? apiTypeToLabel(zone.type) : 'Unknown'}
          </Badge>
          {(() => {
            const now = new Date();
            const beginTime = zone ? new Date(zone.effective_begin_time) : null;
            const endTime = zone?.effective_end_time ? new Date(zone.effective_end_time) : null;
            const isActive = beginTime && beginTime <= now && (!endTime || endTime >= now);
            return (
              <Badge variant={isActive ? "default" : "secondary"}>
                {isActive ? "Active" : "Inactive"}
              </Badge>
            );
          })()}
          <Badge 
            variant="outline"
            className={`${
              apiTypeToLabel(zone?.type || '') === "Restricted" 
                ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
                : apiTypeToLabel(zone?.type || '') === "Danger"
                ? "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800"
                : apiTypeToLabel(zone?.type || '') === "Prohibited"
                ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
                : ""
            }`}
          >
            {zone?.type ? apiTypeToLabel(zone.type) : 'Unknown'}
          </Badge>
        </div>

        <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
                 {/* Basic Information */}
                 <Card>
                   <CardHeader>
                     <CardTitle className="flex items-center gap-2">
                       <Shield className="h-5 w-5" />
                       Basic Information
                     </CardTitle>
                     <CardDescription>
                       Core details about this flight restriction zone
                     </CardDescription>
                   </CardHeader>
                   <CardContent className="space-y-4">
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                       <div>
                         <label className="text-sm font-medium text-muted-foreground">Designator</label>
                         <p className="text-sm">{zone?.designator}</p>
                       </div>
                       <div>
                         <label className="text-sm font-medium text-muted-foreground">ICAO Designator</label>
                         <p className="text-sm">{zone?.designator_icao}</p>
                       </div>
                       <div>
                         <label className="text-sm font-medium text-muted-foreground">Type</label>
                         <p className="text-sm">
                           <Badge variant="outline" className="text-xs">
                             {zone?.type ? apiTypeToLabel(zone.type) : 'Unknown'}
                           </Badge>
                         </p>
                       </div>
                     </div>
                   </CardContent>
                 </Card>

          {/* Effective Dates */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Effective Dates
              </CardTitle>
              <CardDescription>
                When this zone is active and enforced
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Effective From</label>
                  <p className="text-sm flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {zone?.effective_begin_time ? formatDate(zone.effective_begin_time) : "Not specified"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Effective To</label>
                  <p className="text-sm flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {zone?.effective_end_time ? formatDate(zone.effective_end_time) : "No expiry"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Zone Map */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Zone Location
              </CardTitle>
              <CardDescription>
                Geographic boundaries of this flight restriction zone
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ZoneDetailMap
                zoneResponse={zone ? {
                  type: 'Feature' as const,
                  id: zone.id,
                  geometry: {
                    type: 'MultiPolygon' as const,
                    coordinates: zone.polygon
                  },
                  properties: zone
                } : null}
                className="h-64 w-full"
              />
            </CardContent>
          </Card>

                 {/* Geometry Information */}
                 <Card>
                   <CardHeader>
                     <CardTitle className="flex items-center gap-2">
                       <MapPin className="h-5 w-5" />
                       Geometry Information
                     </CardTitle>
                     <CardDescription>
                       Spatial data and coverage details
                     </CardDescription>
                   </CardHeader>
                   <CardContent className="space-y-4">
                     <div className="space-y-4">
                       <div>
                         <label className="text-sm font-medium text-muted-foreground">Altitude Limits</label>
                         <p className="text-sm">
                           {zone?.lower_limit || 'N/A'} {zone?.lower_limit_uom || ''} - {zone?.upper_limit || 'N/A'} {zone?.upper_limit_uom || ''}
                         </p>
                       </div>
                     </div>
                   </CardContent>
                 </Card>
        </div>

        {/* Additional Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Additional Information
            </CardTitle>
            <CardDescription>
              Regulatory and descriptive information about this zone
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {zone?.notes && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <p className="text-sm">{zone.notes[0]?.text as string ?? 'No description available'}</p>
              </div>
            )}
            {zone?.notes && Array.isArray(zone.notes) && zone.notes.length > 0 && (
              <ZoneNotes notes={zone.notes} />
            )}
            {zone?.local_type && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Local Type</label>
                <p className="text-sm">{zone.local_type}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Information */}
        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Flight Restriction Zone Details</p>
              <p>
                This page shows comprehensive information about the selected flight restriction zone. 
                Use the map view to see the zone's geographic boundaries, or check violations 
                to see any drone activity that has occurred in this area.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </main>
  );
};
